import { DEMO_CALCULATOR_STEP } from 'config/constants'
import { DemoPlayerContext } from './player-clock'
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import {
  DEMO_ANIMATION_START_STEP,
  DEMO_CALCULATOR_STEPS,
} from './playback-constants'

const DEMO_EXPANDED_STEPS = DEMO_CALCULATOR_STEPS.reduce<CalculatorExpandedSteps>(
  (steps, step) => ({
    ...steps,
    [step]: step === DEMO_ANIMATION_START_STEP,
  }),
  {
    additional_services: false,
    banking_commissions: false,
    certification: false,
    customs: false,
    delivery: false,
    main: false,
    packaging: false,
  },
)

export const useDemoCalculatorPlayback = ({
  expandedSteps,
  isDemoMode,
  setExpandedSteps,
  translate,
}: UseDemoCalculatorPlaybackParams) => {
  const player = useContext(DemoPlayerContext)
  const isPlayer = Boolean(player)
  const startIndex = player?.startStep ?? 0
  const startStep = DEMO_CALCULATOR_STEPS[startIndex]

  const [ lastVisibleStep, setLastVisibleStep ] = useState<DemoCalculatorStep>(
    startStep,
  )
  const [ isFirstStepComplete, setIsFirstStepComplete ] = useState(startIndex > 0)
  const [ isFirstStepCollapsed, setIsFirstStepCollapsed ] = useState(
    startIndex > 0,
  )
  const [ isPackagingComplete, setIsPackagingComplete ] = useState(startIndex > 1)
  const [ isDeliveryComplete, setIsDeliveryComplete ] = useState(startIndex > 2)
  const [ isCustomsComplete, setIsCustomsComplete ] = useState(startIndex > 3)
  const [ isCertificationComplete, setIsCertificationComplete ] = useState(startIndex > 4)
  const [ isBankCommissionComplete, setIsBankCommissionComplete ] = useState(startIndex > 5)
  const [ isAdditionalServicesComplete, setIsAdditionalServicesComplete ] = useState(startIndex > 6)
  const [ isSummaryVisible, setIsSummaryVisible ] = useState(false)

  useEffect(() => {
    if (isDemoMode) {
      setExpandedSteps({ ...DEMO_EXPANDED_STEPS, main: startIndex === 0, [startStep]: true })
    }
  }, [ isDemoMode, setExpandedSteps, startIndex, startStep ])

  useEffect(() => {
    player?.onStep(DEMO_CALCULATOR_STEPS.indexOf(lastVisibleStep))
  }, [ lastVisibleStep, player?.onStep ])

  useLayoutEffect(() => {
    if (!isDemoMode || lastVisibleStep !== DEMO_CALCULATOR_STEP.PACKAGING) {
      return
    }

    const packagingStep = document.querySelector<HTMLElement>('[data-calculator-step="packaging"]')

    if (!packagingStep) {
      return
    }

    const updateFieldStates = () => {
      const fields = packagingStep.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >('input, select, textarea')

      fields.forEach(field => {
        const isEditableField = field.hasAttribute('data-demo-editable')
        const shouldDisableField = isPackagingComplete && !isEditableField

        if (field.disabled !== shouldDisableField) {
          field.disabled = shouldDisableField
        }
      })
    }

    updateFieldStates()

    if (isPackagingComplete) {
      return
    }

    const observer = new MutationObserver(updateFieldStates)

    observer.observe(packagingStep, {
      attributeFilter: [ 'disabled' ],
      attributes: true,
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [ isDemoMode, isPackagingComplete, lastVisibleStep ])

  const changeExpandedSteps = (steps: Record<string, boolean>) => {
    if (!isDemoMode) {
      setExpandedSteps({ ...expandedSteps, ...steps })
      return
    }

    setExpandedSteps(currentSteps => ({ ...currentSteps, ...steps }))
  }

  const goToNextStep = (stepsToClose: DemoCalculatorStep[], nextStep: DemoCalculatorStep) => {
    setLastVisibleStep(nextStep)
    setExpandedSteps(currentSteps => ({
      ...currentSteps,
      ...stepsToClose.reduce((steps, step) => ({ ...steps, [step]: false }), {}),
      [nextStep]: true,
    }))
  }

  const completeFirstStep = useCallback(() => {
    setIsFirstStepComplete(true)
    if (!isPlayer) {
      setExpandedSteps(currentSteps => ({ ...currentSteps, main: false }))
    }
  }, [ setExpandedSteps, isPlayer ])

  const completeFirstStepCollapse = useCallback(() => {
    const shouldWaitForPlayback = !isDemoMode || !isFirstStepComplete

    if (shouldWaitForPlayback || isFirstStepCollapsed) {
      return
    }

    setIsFirstStepCollapsed(true)
    setLastVisibleStep(DEMO_CALCULATOR_STEP.PACKAGING)
    setExpandedSteps(currentSteps => ({ ...currentSteps, packaging: true }))
  }, [ isDemoMode, isFirstStepCollapsed, isFirstStepComplete, setExpandedSteps ])

  const completePackagingStep = useCallback(() => {
    setIsPackagingComplete(true)
    setExpandedSteps(currentSteps => ({ ...currentSteps, packaging: true }))
  }, [ setExpandedSteps ])

  const completeDeliveryStep = useCallback(() => setIsDeliveryComplete(true), [])
  const completeBankCommissionStep = useCallback(() => setIsBankCommissionComplete(true), [])
  const completeAdditionalServicesStep = useCallback(() => setIsAdditionalServicesComplete(true), [])

  const completeCustomsStep = () => {
    changeExpandedSteps({ customs: true, invoices: true })
    setIsCustomsComplete(true)
  }

  const completeCertificationStep = () => {
    changeExpandedSteps({ certification: true })
    setIsCertificationComplete(true)
  }

  const showSummary = () => {
    setIsSummaryVisible(true)
    setExpandedSteps(currentSteps => ({ ...currentSteps, additional_services: false }))
  }

  useEffect(() => {
    if (player?.summaryVisible) {
      showSummary()
    }
  }, [ player?.summaryVisible ])

  const isStepVisible = (step: DemoCalculatorStep) => {
    if (!isDemoMode) {
      return true
    }

    return DEMO_CALCULATOR_STEPS.indexOf(step) <= DEMO_CALCULATOR_STEPS.indexOf(lastVisibleStep)
  }

  const isStepCompleted = (step: DemoCalculatorStep, backendValue?: boolean) => {
    if (!isDemoMode) {
      return backendValue
    }

    if (step === DEMO_CALCULATOR_STEP.MAIN && isFirstStepComplete) {
      return true
    }

    if (step === DEMO_CALCULATOR_STEP.PACKAGING && isPackagingComplete) {
      return true
    }

    if (step === DEMO_CALCULATOR_STEP.DELIVERY && isDeliveryComplete) {
      return true
    }

    if (step === DEMO_CALCULATOR_STEP.CUSTOMS && isCustomsComplete) {
      return true
    }

    if (step === DEMO_CALCULATOR_STEP.CERTIFICATION && isCertificationComplete) {
      return true
    }

    if (step === DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS && isBankCommissionComplete) {
      return true
    }

    if (step === DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES && isAdditionalServicesComplete) {
      return true
    }

    return DEMO_CALCULATOR_STEPS.indexOf(step) < DEMO_CALCULATOR_STEPS.indexOf(lastVisibleStep)
  }

  const getStepId = (demoId: string, defaultId: string) => {
    return isDemoMode ? demoId : defaultId
  }

  const getStepButtons = (
    stepsToClose: DemoCalculatorStep[],
    nextStep?: DemoCalculatorStep,
  ) => {
    const usesInlineNavigation = isDemoMode && !isPlayer

    if (!usesInlineNavigation || !nextStep) {
      return null
    }

    const pendingSteps: Partial<Record<DemoCalculatorStep, boolean>> = {
      additional_services: !isBankCommissionComplete,
      banking_commissions: !isCertificationComplete,
      certification: !isCustomsComplete,
      customs: !isDeliveryComplete,
      delivery: !isPackagingComplete,
    }

    if (pendingSteps[nextStep] || isStepVisible(nextStep)) {
      return null
    }

    return (
      <div className='calculator__demo-step-buttons'>
        <Button
          data-demo-allowed='next-step'
          data-demo-playback-action={nextStep}
          className='calculator__demo-step-button'
          variant='contained'
          onClick={() => goToNextStep(stepsToClose, nextStep)}
        >
          <Typography variant='button'>{translate('actions__text__next')}</Typography>
        </Button>
      </div>
    )
  }

  const currentStepCompleted = isStepCompleted(lastVisibleStep)
  const onStepComplete = player?.onStepComplete

  useEffect(() => {
    if (currentStepCompleted) {
      onStepComplete?.(DEMO_CALCULATOR_STEPS.indexOf(lastVisibleStep))
    }
  }, [ currentStepCompleted, lastVisibleStep, onStepComplete ])
  const isCurrentDemoCustoms = isDemoMode && lastVisibleStep === DEMO_CALCULATOR_STEP.CUSTOMS
  const hasCompletedCurrentCustoms = isCurrentDemoCustoms && isCustomsComplete
  const shouldExpandCustoms = expandedSteps.customs || hasCompletedCurrentCustoms
  const usesInlineNavigation = isDemoMode && !isPlayer
  const hasPendingSummary = isAdditionalServicesComplete && !isSummaryVisible
  const shouldShowAdditionalServicesNext = usesInlineNavigation && hasPendingSummary

  let packagingState: 'active' | 'complete' | undefined

  if (isDemoMode) {
    packagingState = isPackagingComplete ? 'complete' : 'active'
  }

  return {
    calculatorProps: {
      'data-demo-current-step-animation': isDemoMode && !currentStepCompleted
        ? 'active'
        : undefined,
      'data-demo-first-step-playback': isDemoMode && !isFirstStepCollapsed ? 'active' : undefined,
      'data-demo-packaging-playback': packagingState,
    },
    changeExpandedSteps,
    completeAdditionalServicesStep,
    completeBankCommissionStep,
    completeCertificationStep,
    completeCustomsStep,
    completeDeliveryStep,
    completeFirstStep,
    completeFirstStepCollapse,
    completePackagingStep,
    getStepButtons,
    getStepId,
    isAdditionalServicesDisabled: isDemoMode ? !isBankCommissionComplete : undefined,
    isStepCompleted,
    isStepVisible,
    playbackProps: {
      onCompleteAdditionalServicesStep: completeAdditionalServicesStep,
      onCompleteBankCommissionStep: completeBankCommissionStep,
      onCompleteDeliveryStep: completeDeliveryStep,
      onCompleteFirstStep: completeFirstStep,
      onCompletePackagingStep: completePackagingStep,
    },
    rightPanelProps: {
      demoCurrentStepCompleted: isDemoMode ? currentStepCompleted : undefined,
      demoCurrentStepIndex: isDemoMode ? DEMO_CALCULATOR_STEPS.indexOf(lastVisibleStep) : undefined,
    },
    shouldExpandCustoms,
    shouldShowAdditionalServicesNext,
    showSummary,
    summaryIsVisible: isDemoMode ? isSummaryVisible : undefined,
  }
}
